import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  PermissionScope,
  permissionKeySchema,
  permissionScopeSchema,
  type PermissionKeyEnum,
  type PermissionScopeEnum,
} from "@fitness-app/data-models";

import { type AsyncThunkCreator } from "../../../index";
import { TEAM_REDUCER_NAME } from "../types";

interface Payload {
  memberId: string;
  key: PermissionKeyEnum;
  scope: PermissionScopeEnum | null;
}

const invalidGrantMessage = "Nieprawidłowe nadpisanie uprawnienia.";
const missingGrantMutationMessage = "Nie udało się zmienić nadpisania uprawnienia.";

export const setTeamMemberPermissionGrant = createAsyncThunk<void, Payload, AsyncThunkCreator<string>>(
  `${TEAM_REDUCER_NAME}/setTeamMemberPermissionGrant`,
  async ({ memberId, key, scope }, { rejectWithValue, extra: { db } }) => {
    const parsedKey = permissionKeySchema.safeParse(key);
    const parsedScope = scope === null ? null : permissionScopeSchema.safeParse(scope);

    if (
      !parsedKey.success ||
      (parsedScope !== null && (!parsedScope.success || parsedScope.data === PermissionScope.NONE))
    ) {
      return rejectWithValue(invalidGrantMessage);
    }

    if (parsedScope === null) {
      const { data, error } = await db
        .from("team_member_permission")
        .delete()
        .eq("memberId", memberId)
        .eq("key", parsedKey.data)
        .select("key")
        .maybeSingle();

      if (error) {
        return rejectWithValue(error.message);
      }

      if (!data) {
        return rejectWithValue(missingGrantMutationMessage);
      }

      return;
    }

    const { data, error } = await db
      .from("team_member_permission")
      .upsert(
        {
          memberId,
          key: parsedKey.data,
          scope: parsedScope.data,
          updatedAt: new Date().toISOString(),
        },
        { onConflict: "memberId,key" },
      )
      .select("key")
      .maybeSingle();

    if (error) {
      return rejectWithValue(error.message);
    }

    if (!data) {
      return rejectWithValue(missingGrantMutationMessage);
    }
  },
);
